import React, { useState } from "react";
import { useHabits } from "@/context/SupabaseHabitContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { format, subDays, addDays, isToday } from "date-fns";
import { CalendarCheck, Eye, EyeOff, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import GridConfetti from "@/components/animations/GridConfetti";
import { getLocalDateString } from "@/utils/dateUtils";

interface DailyChecklistProps {
  showPausedHabits?: boolean;
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  onToggleShowPausedHabits?: (show: boolean) => void;
}

interface ContextMenuProps {
  x: number;
  y: number;
  habitName: string;
  onClose: () => void;
  onViewInGrid: () => void;
}

const ContextMenu = ({ x, y, habitName, onClose, onViewInGrid }: ContextMenuProps) => {
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50" 
        onClick={onClose}
      />
      
      {/* Context Menu */}
      <div 
        className="fixed z-50 bg-background border rounded-md shadow-lg min-w-[140px]"
        style={{ left: `${x}px`, top: `${y}px`, margin: 0, padding: 0 }}
      >
        <button
          className="w-full px-3 py-1 text-left hover:bg-accent flex items-center gap-1.5 text-xs rounded-md"
          style={{ margin: 0 }}
          onClick={onViewInGrid}
        >
          <Calendar className="h-3 w-3" />
          View in grid
        </button>
      </div>
    </>
  );
};

export default function DailyChecklist({ 
  showPausedHabits = false, 
  selectedDate: propSelectedDate,
  onDateChange,
  onToggleShowPausedHabits 
}: DailyChecklistProps) {
  const { habits, toggleHabitCompletion, isHabitCompletedOnDate } = useHabits();
  const [internalSelectedDate, setInternalSelectedDate] = useState(new Date());
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    habitId: string;
    habitName: string;
  } | null>(null);
  
  // Use prop selectedDate if provided, otherwise use internal state
  const selectedDate = propSelectedDate || internalSelectedDate;
  const setSelectedDate = onDateChange || setInternalSelectedDate;
  
  // Use local timezone date string to avoid UTC conversion issues
  const dateString = getLocalDateString(selectedDate);
  const [isUpdating, setIsUpdating] = useState<{ [key: string]: boolean }>({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSource, setConfettiSource] = useState<{ x: number; y: number } | undefined>(undefined);
  
  // Filter habits based on showPausedHabits setting
  const filteredHabits = habits.filter(habit => {
    if (habit.name.trim() === '') return false;
    if (showPausedHabits) return true; // Show all habits including paused
    return habit.active !== false; // Only show active habits
  });
  
  // Handle right-click context menu
  const handleContextMenu = (event: React.MouseEvent, habitId: string, habitName: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Context menu dimensions (smaller now)
    const menuWidth = 180;
    const menuHeight = 40;
    
    // Calculate position very close to click, ensuring menu stays within viewport
    let x = event.clientX + 2; // Just a tiny offset to avoid blocking the cursor
    let y = event.clientY; // No vertical offset - appear right at click position
    
    // Adjust horizontal position if menu would go off-screen
    if (x + menuWidth > viewportWidth) {
      x = event.clientX - menuWidth - 2; // Place to the left instead
    }
    
    // Adjust vertical position if menu would go off-screen
    if (y + menuHeight > viewportHeight) {
      y = event.clientY - menuHeight; // Place above with no extra offset
    }
    
    setContextMenu({
      x,
      y,
      habitId,
      habitName
    });
  };
  
  // Handle scroll to grid
  const handleViewInGrid = () => {
    if (!contextMenu) return;
    
    // Find the specific habit's card element
    const habitElement = document.querySelector(`#habit-${contextMenu.habitId}`);
    if (habitElement) {
      habitElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    } else {
      // Fallback to combined grid if individual habit card not found
      const gridElement = document.querySelector('.combined-habit-grid');
      if (gridElement) {
        gridElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
    setContextMenu(null);
  };
  
  // Close context menu
  const closeContextMenu = () => {
    setContextMenu(null);
  };

  if (filteredHabits.length === 0) {
    return (
      <Card className="mb-8">
        <CardContent className="py-8">
          <div className="text-center">
            <CalendarCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Ready to build some habits?</h3>
            <p className="text-muted-foreground">
              Add habits to start! 🚀
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const handleToggle = async (habitId: string, clickPosition?: { x: number; y: number }) => {
    // Set updating state for this habit
    setIsUpdating(prev => ({ ...prev, [habitId]: true }));
    
    try {
      // Only trigger confetti for today's date and if marking as complete
      const isCompletingToday = isToday(selectedDate) && !isHabitCompletedOnDate(habitId, dateString);
      
      if (isCompletingToday && clickPosition) {
        // Use the provided click position for confetti source
        setConfettiSource(clickPosition);
      }
      
      await toggleHabitCompletion(
        habitId, 
        dateString, 
        isCompletingToday ? () => setShowConfetti(true) : undefined
      );
    } finally {
      // Clear updating state
      setIsUpdating(prev => ({ ...prev, [habitId]: false }));
    }
  };

  const handlePreviousDay = () => {
    const newDate = subDays(selectedDate, 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    // Don't allow going beyond today
    const tomorrow = addDays(new Date(), 1);
    if (selectedDate < tomorrow) {
      const newDate = addDays(selectedDate, 1);
      setSelectedDate(newDate);
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };
  
  return (
    <>
      <Card className="mb-8">
        <CardHeader className="pb-3">
          {/* Mobile: stacked layout */}
          <div className="block sm:hidden space-y-3">
            {/* Top row: Title and date navigation */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <CalendarCheck className="text-primary h-5 w-5" />
                <CardTitle className="text-xl">Daily Checklist</CardTitle>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={handlePreviousDay}
                  className="p-1 rounded hover:bg-accent"
                  aria-label="Previous day"
                >
                  &larr;
                </button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleToday}
                  className="h-8 text-xs"
                >
                  Today
                </Button>
                <button 
                  onClick={handleNextDay}
                  disabled={isToday(selectedDate)}
                  className="p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next day"
                >
                  &rarr;
                </button>
              </div>
            </div>
            
            {/* Bottom row: Show/Hide paused button */}
            {onToggleShowPausedHabits && (
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => onToggleShowPausedHabits(!showPausedHabits)}
                  title={showPausedHabits ? "Hide paused habits" : "Show paused habits"}
                >
                  {showPausedHabits ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  {showPausedHabits ? "Hide Paused" : "Show Paused"}
                </Button>
              </div>
            )}
          </div>

          {/* Desktop: original single row layout */}
          <div className="hidden sm:flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CalendarCheck className="text-primary h-5 w-5" />
              <CardTitle className="text-xl">Daily Checklist</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {onToggleShowPausedHabits && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => onToggleShowPausedHabits(!showPausedHabits)}
                  title={showPausedHabits ? "Hide paused habits" : "Show paused habits"}
                >
                  {showPausedHabits ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  {showPausedHabits ? "Hide Paused" : "Show Paused"}
                </Button>
              )}
              <div className="flex items-center space-x-2">
                <button 
                  onClick={handlePreviousDay}
                  className="p-1 rounded hover:bg-accent"
                  aria-label="Previous day"
                >
                  &larr;
                </button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleToday}
                  className="h-8 text-xs"
                >
                  Today
                </Button>
                <button 
                  onClick={handleNextDay}
                  disabled={isToday(selectedDate)}
                  className="p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next day"
                >
                  &rarr;
                </button>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {isToday(selectedDate) ? 'Today' : ''} {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
            {filteredHabits.map((habit) => {
              const isCompleted = isHabitCompletedOnDate(habit.id, dateString);
              return (
                <div 
                  key={habit.id} 
                  className="flex items-center space-x-2 py-1 cursor-pointer"
                  onClick={(event) => {
                    // Capture click position
                    const rect = event.currentTarget.getBoundingClientRect();
                    const clickPosition = {
                      x: rect.left + rect.width / 2,
                      y: rect.top + rect.height / 2
                    };
                    handleToggle(habit.id, clickPosition);
                  }}
                  onContextMenu={(event) => handleContextMenu(event, habit.id, habit.name)}
                >
                    <Checkbox 
                      id={`checklist-${habit.id}`}
                      checked={isCompleted}
                      onCheckedChange={() => {}} // Prevent default behavior, handle in wrapper div
                      disabled={isUpdating[habit.id]}
                      style={{
                        backgroundColor: isCompleted ? habit.color.filled : 'transparent',
                          borderColor: habit.color.filled
                      }}
                      className="ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pointer-events-none"
                    />
                    <label
                      htmlFor={`checklist-${habit.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 pointer-events-none"
                    >
                      {habit.name}
                      {isUpdating[habit.id] && (
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent"></span>
                      )}
                    </label>
                </div>
              );
            })}
          </div>
        </CardContent>
        <GridConfetti 
          run={showConfetti} 
          onConfettiComplete={() => setShowConfetti(false)} 
          sourcePosition={confettiSource}
        />
      </Card>
      
      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          habitName={contextMenu.habitName}
          onClose={closeContextMenu}
          onViewInGrid={handleViewInGrid}
        />
      )}
    </>
  );
}
